import * as THREE from 'three';

import AbstractGenerator from '../../../misc/generator';
import AbstractObject from '../../../misc/object';

import Ground from '../objects/ground';
import Panel from '../../../objects/panel';
import Barrier from '../objects/barrier';
import Arrow from '../../../objects/arrow';
import { HTMLEditableLabel } from '../../../objects/html-label';
import { addDistanceLabel, getEndpoints, getSides } from '../../../../services/helper.service';
import { unitTransform } from '@services/helper.service';
import { defaultUnits } from '@config';

import Scene from '@scene';

import { generateScrewGroup } from './helper';
import { LabelsPadding } from '@scene/objects/ground';

export default class BetweenWalls extends AbstractGenerator {

  constructor(options, dispatcher) {
    super(options);

    this.options.ground.type = Ground.types.WALLS;
    this.options.barrier.type = Barrier.types.WALLS;
    this.options.barrier.tilt = 0;

    this.dispatchOptionUpdate = dispatcher;

    this.panels = new AbstractObject();

    if (this.options.ground.padding.hasOwnProperty('C1x')) 
      this.options.ground.padding.C2x = this.options.ground.walls.depth - this.options.ground.padding.C1x;
    // } else {
    //   this.options.ground.padding.C1x = this.options.ground.walls.depth / 2;
    //   this.options.ground.padding.C2x = this.options.ground.walls.depth / 2;
    // }
    }
    
    init3DObject() {
      this.layer3D.addObject(this.panels);

      this.barrier = this.getBarrier({
        ...this.options.barrier,
        height: this.options.barrier.height - this.options.barrier.bottomPadding
      }, null, this.DPP);
      this.layer3D.addObject(this.barrier);

      this.options.panel.position = Panel.positions.VERTICAL_Y_INVERT;

      this.barrier.staysPositions.forEach((item, index) => {
        if (index > 1) {
          this.options.panel.position = Panel.positions.VERTICAL_Y;
        }

        const singlePanel = this.getPanel(this.options.panel, new THREE.Vector3(
          item.z,
          item.y,
          item.x
        ));

        if (index > 1) {
          this.layer3D.addObject(singlePanel);
        } 
        if (index === 3) {
          this.topPanel = singlePanel;
        } 

        this.panels.object.addObject(singlePanel);
        this.singlePanel = singlePanel;
      });

      this.options.ground.walls.spaceBetween = this.barrier.size.width;
      this.options.ground.walls.height = this.barrier.size.height + 500;

      this.ground = this.getGround(this.singlePanel);
      this.layer3D.addObject(this.ground);

      this.panels.mesh.position.set(
        this.ground.mesh.position.x - this.ground.size.wall.depth / 2,
        this.ground.mesh.position.y - this.ground.size.wall.height / 2 + this.options.barrier.bottomPadding + this.barrier.size.height,
        this.ground.mesh.position.z
      );

      this.barrier.mesh.position.x = this.panels.mesh.position.x;
      this.barrier.mesh.position.y = this.panels.mesh.position.y - this.barrier.size.height / 2;
      this.barrier.mesh.position.z = this.panels.mesh.position.z;

      if (this.options.beam.shift) {
        this.barrier.mesh.position.x += this.options.beam.shift.x;
        this.barrier.mesh.position.y += this.options.beam.shift.y;
      }

      // Create screw labels
      let panelCenter = new THREE.Vector3();
      this.topPanel.mesh.getWorldPosition(panelCenter);
      const screwLabels = this.screwGroup.getScrewLabels();
      screwLabels.htmlLayer.htmlLayerElements.forEach((htmlElem) => { 
        htmlElem.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI / 2);
        htmlElem.position.applyAxisAngle(new THREE.Vector3(0, 0, 1), -Math.PI / 2);
        htmlElem.position.add(panelCenter);
        htmlElem.position.z += this.topPanel.options.depth / 2;
        htmlElem.optimalCameraDist = 3000;
        htmlElem.zoomFactor = 0.5;
      });
      this.layer3D.addObject(screwLabels);

      if (this.options.object.dimensions.display || this.options.object.dimensions.renderForReport) {
        // display on the report regardless of the display setting
        // Create dpot label
        const { line: dpotLine, htmlElement: dpotHtmlElement } = this.createDpotLabel(this.options.object.dimensions);
        this.layer3D.addMesh(dpotLine);
        this.layer3D.htmlLayer.add(dpotHtmlElement);
  
        // Create hgs label
        const { line: hgsLine, htmlElement: hgsHtmlElement } = this.createHgsLabel(this.options.object.dimensions);
        this.layer3D.addMesh(hgsLine);
        this.layer3D.htmlLayer.add(hgsHtmlElement);
      }
      if (this.options.object.dimensions.display) {
        this.addLabels();
      }
  }

  init2DObject() {
      this.layer2D.addObject(this.panels);

      // Create 2D barrier
      this.barrier = this.getBarrier({
        ...this.options.barrier,
        type: Barrier.types.STAIRS,
        stayCount: 1,
        disableTop: true
      }, null, this.DPP);
      this.layer2D.addObject(this.barrier);

      // Create 2D panel
      this.options.panel.position = Panel.positions.HORIZONTAL_X;

      this.barrier.staysPositions.forEach((item, index) => {
        this.singlePanel = this.getPanel(
          this.options.panel,
          new THREE.Vector3(item.z, this.options.ground.depth, -item.x)
        );

        this.panels.object.addObject(this.singlePanel);
      });
      this.topPanel = this.singlePanel;

      // Create 2D ground object
      this.options.ground.type = Ground.types.FLAT;
      this.options.ground.height = 500;

      this.ground = this.getGround(this.singlePanel);
      this.ground.ground.position.x = this.ground.size.width / -2;
      this.ground.ground.position.y = this.ground.size.height / -2;
      this.ground.ground.position.z = this.ground.size.depth / -2;
      this.layer2D.addObject(this.ground);

      const GROUND_TOP = this.ground.position.y + this.ground.size.width / 2;

      this.panels.mesh.position.set(
        this.ground.position.x,
        GROUND_TOP + this.options.panel.depth / 2,
        this.ground.position.z
      );

      const { compressionLine } = this.singlePanel.getCompressionLine();
      if (compressionLine) {
        compressionLine.mesh.rotation.copy(this.singlePanel.rotation);
        compressionLine.mesh.position.copy(this.panels.position);
        this.layer2D.addObject(compressionLine);
        let zonesLayer = this.singlePanel.createZonesLabels();
        if (zonesLayer) {
          zonesLayer.mesh.rotation.copy(this.singlePanel.rotation);
          zonesLayer.mesh.position.copy(this.panels.position);
          this.layer2D.addLayer(zonesLayer);
        }
      }

      // Update barrier position
      const BARRIER_BOTTOM = this.barrier.size.height;

      this.barrier.position.set(
        this.panels.mesh.position.x,
        this.panels.mesh.position.y + this.options.panel.depth / 2 + BARRIER_BOTTOM + this.barrier.size.height / -2,
        this.panels.mesh.position.z
      );

      this.singlePanel.mesh.rotation.x = 0;
      this.singlePanel.mesh.rotation.y = 0;
      this.singlePanel.mesh.rotation.z = Math.PI / 2;

      this.addLabels();

      if (this.options.beam.shift) {
        this.barrier.mesh.position.x += this.options.beam.shift.x;
        this.barrier.mesh.position.z -= this.options.beam.shift.y;
      }

    }

  addLabels() {
    const layer = this.is3DMode ? this.layer3D : this.layer2D;
    let options = null;
    let screwLabels = null;
    if (this.is3DMode) {
      options = {
        is3DMode: true,
        // rotation: new THREE.Euler(Math.PI / 2, 0, 0), //this.layer3D.mesh.rotation,
        showLabels: this.options.object.dimensions.showLabels,
        displayHorizontally: this.options.object.dimensions.displayHorizontally,
        isSafetyBarrier: true
      };
    } else if (this.is2DMode) {
      screwLabels = this.screwGroup.getScrewLabels();
      layer.addObject(screwLabels);
    }

    let panelCenter = new THREE.Vector3();
    this.topPanel.mesh.getWorldPosition(panelCenter);

    // Create W label
    let wLabel = null;
    if (this.is3DMode) {
      wLabel = this.ground.create3DWLabel(options);
      layer.addMesh(wLabel.object);
    } else {
      wLabel = this.ground.createWLabel();
    }
    layer.htmlLayer.add(wLabel.htmlElement);

    // Create C1x label
    let c1xLabel = null;
    if (this.is3DMode) {
      c1xLabel = this.create3DC1xLabel(
        this.topPanel.size.height / 2 - this.topPanel.options.dy.value, 
        this.topPanel.size.height / 2 + LabelsPadding,
        options
      );
      layer.addMesh(c1xLabel.line);
    } else {
      c1xLabel = this.createC1xLabel(this.topPanel.size.height / 2 - this.topPanel.options.dy.value, this.ground.size.height / 2);
    }
    layer.htmlLayer.add(c1xLabel.htmlElement);

    // Create C2x label
    let c2xLabel = null;
    if (this.is3DMode) {
      c2xLabel = this.create3DC2xLabel(
        this.topPanel.size.height / 2 - this.topPanel.options.dy.value, 
        this.topPanel.size.height / 2 + LabelsPadding,
        options
      );
      layer.addMesh(c2xLabel.line);
    } else {
      c2xLabel = this.createC2xLabel(this.topPanel.size.height / 2 - this.topPanel.options.dy.value, this.ground.size.height / 2);
    }
    layer.htmlLayer.add(c2xLabel.htmlElement);

    // Create ground thickness label
    let groundThickness = null;
    if (this.is3DMode) {
      let groundPos = {
        x: this.ground.position.x + this.ground.size.ground.padding,
        y: this.ground.position.y - this.ground.size.wall.height / 2 - this.ground.size.ground.height,
        z: this.ground.position.z - this.ground.size.wall.space / 2 - this.ground.size.wall.width - LabelsPadding
      };
      groundThickness = this.ground.getThicknessLabel(groundPos, options, true);
      layer.addMesh(groundThickness.arrow);
      groundThickness.htmlElement.position.set(
        groundPos.x,
        groundPos.y + this.ground.size.ground.height / 2,
        groundPos.z
      );
    } else {
      groundThickness = this.ground.getDepthLabel([
        {
          x: -this.ground.size.width / 2 + LabelsPadding,
          y: this.ground.size.height / 2 - LabelsPadding,
          z: 0
        },
        {
          x: -this.ground.size.width / 2 + LabelsPadding - 100,
          y: this.ground.size.height / 2 - LabelsPadding - 100,
          z: 0
        },
        {
          x: -this.ground.size.width / 2 + LabelsPadding - 200,
          y: this.ground.size.height / 2 - LabelsPadding - 100,
          z: 0
        }
      ]);
      groundThickness.htmlElement.position.set(
        -groundThickness.path[1].x,
        groundThickness.path[1].z,
        groundThickness.path[1].y
      );
    }
    layer.htmlLayer.add(groundThickness.htmlElement);
    
    // Create label for Dy
    const { htmlElement, arrow } = this.screwGroup.createDyLabel(null, options);
    if (this.is2DMode) {
      htmlElement.position.set(
        this.panels.mesh.position.x - arrow.position.y,
        this.panels.mesh.position.y,
        this.panels.mesh.position.z + arrow.position.z - this.singlePanel.options.dy.value / 2
      );
    } else {
      htmlElement.position.set(
        panelCenter.x - arrow.position.y,
        panelCenter.y + this.topPanel.options.dy.value / 2 - this.topPanel.size.height / 2,
        panelCenter.z + this.topPanel.size.depth / 2
      );
      htmlElement.optimalCameraDist = 3000;
      htmlElement.zoomFactor = 0.5;
      
      if (!options.displayHorizontally) {
        htmlElement.align = HTMLEditableLabel.types.align.CENTER;
        htmlElement.position.x -= 15;
        htmlElement.setDirection(
          htmlElement.position.clone().setY(htmlElement.position.y + this.topPanel.options.dy.value / 2),
          htmlElement.position.clone().setY(htmlElement.position.y - this.topPanel.options.dy.value / 2),
          false
        );
      }
    }
    layer.htmlLayer.add(htmlElement);

    // Create depth label for panel
    let panelDepth;
    if (this.is3DMode) {
      panelDepth = this.topPanel.create3DDepthLabel(options);
      panelDepth.object.rotation.z = Math.PI; // move on the other side to prevent overlap with DPP label
      this.topPanel.mesh.add(panelDepth.object);
      panelDepth.htmlElement.align = HTMLEditableLabel.types.align.RIGHT;
      panelDepth.htmlElement.position.y = panelCenter.y - panelDepth.htmlElement.position.x;
      panelDepth.htmlElement.position.x = panelCenter.x + panelDepth.htmlElement.position.z;
      panelDepth.htmlElement.position.z = panelCenter.z;
      panelDepth.htmlElement.optimalCameraDist = 3000;
      panelDepth.htmlElement.zoomFactor = 0.5;
    } else {
      panelDepth = this.topPanel.createDepthLabel();
      panelDepth.htmlElement.position.set(
        this.panels.mesh.position.x - panelDepth.path[1].y,
        this.panels.mesh.position.y,
        this.panels.mesh.position.z + panelDepth.path[1].z
      );
    }
    layer.htmlLayer.add(panelDepth.htmlElement);

    // Create width label
    const { line: widthLine, htmlElement: widthHtmlElement } = this.topPanel.createWidthLabel(options);
    this.topPanel.object.addMesh(widthLine);
    if (this.is3DMode) {
      widthHtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
      widthHtmlElement.position.set(
        panelCenter.x,
        panelCenter.y + LabelsPadding + this.topPanel.size.height / 2 + 15,
        panelCenter.z
      );
      widthHtmlElement.optimalCameraDist = 3000;
      widthHtmlElement.zoomFactor = 0.5;
      if (!options.displayHorizontally) {
        widthHtmlElement.setDirection(
          widthHtmlElement.position.clone().setX(widthHtmlElement.position.x + this.topPanel.size.width / 2),
          widthHtmlElement.position.clone().setX(widthHtmlElement.position.x - this.topPanel.size.width / 2),
          false
        );
      }
    }
    layer.htmlLayer.add(widthHtmlElement);

    // Create height label
    const { line: heightLine, htmlElement: heightHtmlElement } = this.topPanel.createHeightLabel(options);
    this.topPanel.object.addMesh(heightLine);
    if (this.is3DMode) {
      heightHtmlElement.position.set(
        panelCenter.x + this.topPanel.size.width / 2 + LabelsPadding,
        panelCenter.y,
        panelCenter.z
      );
      heightHtmlElement.optimalCameraDist = 3000;
      heightHtmlElement.zoomFactor = 0.5;
      if (!options.displayHorizontally) {
        heightHtmlElement.align = HTMLEditableLabel.types.align.CENTER;
        heightHtmlElement.position.x += 15;
        heightHtmlElement.setDirection(
          heightHtmlElement.position.clone().setY(heightHtmlElement.position.y + this.topPanel.size.height / 2),
          heightHtmlElement.position.clone().setY(heightHtmlElement.position.y - this.topPanel.size.height / 2),
          false
          );
      } 
    }
    layer.htmlLayer.add(heightHtmlElement);

    if (this.singlePanel.options.type === Panel.types.SQUARE) {
      // We don't have an anchor pattern with n > 1
      // if (this.options.screw.count.n > 1) {
      //   // Create S1 label
      //   const { object: s1Object, htmlElement: s1HtmlElement } = this.singlePanel.createS1Label(
      //     this.screwGroup,
      //     false, 
      //     LabelsPadding, 
      //     this.singlePanel.size.height / 2 - this.singlePanel.options.dy.value
      //   );
      //   this.singlePanel.object.addMesh(s1Object);
      //   layer.htmlLayer.add(s1HtmlElement);
      // }

      if (this.options.screw.count.m > 1) {
        // Create S2 label
        const { object: s2Object, htmlElement: s2HtmlElement } = this.topPanel.createS2Label(
          this.screwGroup,
          true, 
          this.topPanel.size.width / 2  + LabelsPadding, 
          this.topPanel.size.height / 2 - this.topPanel.options.dy.value,
          options
        );
        if (s2HtmlElement) {
          if (this.is3DMode) {
            s2HtmlElement.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
            s2HtmlElement.position.set(
              panelCenter.x - this.topPanel.size.width / 2 - LabelsPadding,
              panelCenter.y - this.topPanel.size.height / 2 
                + this.topPanel.options.dy.value + this.screwGroup.options.padding.s2 / 2,
              panelCenter.z + this.topPanel.size.depth / 2
            );
            s2HtmlElement.optimalCameraDist = 3000;
            s2HtmlElement.zoomFactor = 0.5;
            if (!options.displayHorizontally) {
              s2HtmlElement.align = HTMLEditableLabel.types.align.CENTER;
              s2HtmlElement.position.x -= 15;
              s2HtmlElement.setDirection(
                s2HtmlElement.position.clone().setY(s2HtmlElement.position.y + this.screwGroup.options.padding.s2 / 2),
                s2HtmlElement.position.clone().setY(s2HtmlElement.position.y - this.screwGroup.options.padding.s2 / 2),
                false
              );
            }
          }
          this.topPanel.object.addMesh(s2Object);
          layer.htmlLayer.add(s2HtmlElement);
        }
      }
    }
    
    if (this.options.beam.shift) {
      this.createShiftLabels(layer, options);
    }
  }

  get DPP() {
    return this.options.panel.dpp.active ? this.options.panel.dpp.value : 0;
  }

  setOrientation() {
    super.setOrientation();

    if (this.is2DMode) {
      this.layer2D.mesh.rotation.x = 0;
      this.layer2D.mesh.rotation.y = 0;
      this.layer2D.mesh.rotation.z = 0;
    }
  }

  createDppLabel(parent) {
    const arrow = new Arrow({
      size: -this.options.panel.dpp.value,
      position: {
        x: 0,
        y: -this.options.panel.width / 2 - LabelsPadding,
        z: this.options.panel.height / -2
      },
      direction: Arrow.types.direction.Z,
      disableDepthTest: true
    });

    parent.object.addObject(arrow);

    const htmlElement = new HTMLEditableLabel({
      text: `Dpp: ${unitTransform(+this.options.panel.dpp.value).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      value: unitTransform(+this.options.panel.dpp.value).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      minValue: 0
    });
    htmlElement.onInputChange((value, didChange) => {
      this.dispatchOptionUpdate({
        panel: {
          dpp: {
            value: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        }
      },
      false,
      didChange);
    });
    htmlElement.position.set(
      this.panels.position.z + this.options.panel.width / 2 + LabelsPadding,
      this.panels.position.x,
      -this.panels.position.y - this.options.panel.height / 2 - this.options.panel.dpp.value / 2
    );

    return {
      arrow,
      htmlElement
    };
  }

  getGround(panel) {
    this.screwPositions = getEndpoints(this.screwGroup.screwCoordinates);

    this.groundPadding = getSides({ ground: this.options.ground, panel: this.options.panel }, this.screwPositions);

    // width it is depth in 2D mode
    this.options.ground.width = this.options.ground.walls.depth;//this.groundPadding.bottom + this.groundPadding.top;

    // if (this.options.ground.padding.C1x && this.options.ground.padding.C2x) {
    //   this.options.ground.width = this.groundPadding.left + this.groundPadding.right;
    // }

    const ground = new Ground(this.options.ground);
    ground.onOptionUpdate(this.dispatchOptionUpdate);
    ground.mesh.rotation.y = Math.PI;
    ground.mesh.rotation.x = Math.PI / 2;

    return ground;
  }

  getPanel(options, position) {
    if (!options) throw new Error('Panel options is required');
    if (!(position instanceof THREE.Vector3)) {
      throw new Error('Position should be instance of THREE.Vector3');
    }

    const panel = new Panel({
      ...options
    });
    panel.onOptionUpdate(this.dispatchOptionUpdate);
    if (position) {
      panel.mesh.position.set(position.x, position.y, position.z);
    }

    this.screwGroup = generateScrewGroup(panel, this.options, this.dispatchOptionUpdate);
    // move the screw group based on C1x edge
    this.screwGroup.screwCoordinates.forEach((coord) => {
      coord.y = this.options.ground.walls.depth / 2 - this.options.ground.padding.C1x;
    });
    this.screwGroup.screws.forEach((screw) => {
      screw.setPosition(new THREE.Vector3(
        screw.position.x, 
        this.options.ground.walls.depth / 2 - this.options.ground.padding.C1x, 
        screw.position.z)
      );
    });

    return panel;
  }

  getBarrier(options, position) {
    if (!options) throw new Error('Barrier options is required');

    const barrier = new Barrier(options);
    barrier.mesh.rotation.y = Math.PI / 2;
    if (position) {
      barrier.mesh.position.set(position.x, position.y, position.z);
    }

    return barrier;
  }

  // startY = screw Y position
  // endY = bottom ground
  createC1xLabel(startY, endY) {
    const { left } = this.screwPositions;

    const { object: line, text: htmlElement } = addDistanceLabel({
      top: {
        from: {
          y: left.y,
          z: startY//startY + LabelsPadding//bottom.z
        },
        to: {
          y: left.y,
          z: endY + LabelsPadding//bottom.z + this.ground.size.height / 8 + (this.ground.options.padding.C1y || 0)
        }
      },
      bottom: {
        from: {
          y: (this.options.ground.walls.depth / 2),
          z: endY
        },
        to: {
          y: (this.options.ground.walls.depth / 2),
          z: endY + LabelsPadding
        }
      },
      size: this.ground.options.padding.C1x || 0, // (this.ground.size.width / 2) - left.y,
      arrowDirection: Arrow.types.direction.Y,
      labelText: `C1x = ${unitTransform(+this.ground.options.padding.C1x).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.ground.options.padding.C1x).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.TOP,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          ground: {
            padding: {
              C1x: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        },
        false,
        didChange);
      }
    });

    line.rotation.z = Math.PI / -2;
    line.rotation.x = Math.PI / -2;
    this.ground.object.addMesh(line);

    return {
      line,
      htmlElement
    };
  }

  create3DC1xLabel(startY, endY, options) {
    const { left } = this.screwPositions;
    let panelCenter = new THREE.Vector3();
    this.topPanel.mesh.getWorldPosition(panelCenter);

    const { object: line, text: htmlElement } = addDistanceLabel({
      top: {
        from: {
          x: panelCenter.x - left.y,
          y: panelCenter.y - startY,
          z: panelCenter.z
        },
        to: {
          x: panelCenter.x - left.y,
          y: panelCenter.y - endY - LabelsPadding,
          z: panelCenter.z
        }
      },
      bottom: {
        from: {
          x: 0,
          y: 0,
          z: 0
        },
        to: {
          x: 0,
          y: 0,
          z: 0
        }
      },
      size: this.ground.options.padding.C1x || 0,
      arrowDirection: Arrow.types.direction.NegativeX,
      labelText: (!options || !options.is3DMode || options.showLabels ? 'C1x = ' : '') + 
        `${unitTransform(+this.ground.options.padding.C1x).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.ground.options.padding.C1x).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          ground: {
            padding: {
              C1x: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        },
        false,
        didChange);
      }
    });

    htmlElement.position.set(
      panelCenter.x - left.y - this.ground.options.padding.C1x / 2,
      panelCenter.y - endY - LabelsPadding - 15,
      panelCenter.z
    );

    htmlElement.optimalCameraDist = 3000;
    htmlElement.zoomFactor = 0.5;
    if (!options.displayHorizontally) {
      htmlElement.setDirection(
        htmlElement.position.clone().setX(htmlElement.position.x + Math.max(25, this.ground.options.padding.C1x / 2)),
        htmlElement.position.clone().setX(htmlElement.position.x - Math.max(25, this.ground.options.padding.C1x / 2)),
        false
      );
    }

    return {
      line,
      htmlElement
    };
  }

  // startY = screw position
  // endY = bottom ground
  createC2xLabel(startY, endY) {
    const { right } = this.screwPositions;

    const { object: line, text: htmlElement } = addDistanceLabel({
      top: {
        from: {
          y: right.y,
          z: startY//bottom.z
        },
        to: {
          y: right.y, 
          z: endY + LabelsPadding//bottom.z + this.ground.size.height / 8 + (this.ground.options.padding.C1y || 0)
        }
      },
      bottom: {
        from: {
          y: -(this.options.ground.walls.depth / 2),
          z: endY
        },
        to: {
          y: -(this.options.ground.walls.depth / 2),
          z: endY + LabelsPadding
        }
      },
      size: -this.ground.options.padding.C2x || 0,
      arrowDirection: Arrow.types.direction.Y,
      labelText: `C2x = ${unitTransform(+this.ground.options.padding.C2x).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.ground.options.padding.C2x).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.TOP,
      onChange: () => {}
    });

    line.rotation.z = Math.PI / -2;
    line.rotation.x = Math.PI / -2;
    this.ground.object.addMesh(line);

    return {
      line,
      htmlElement
    };
  }

  create3DC2xLabel(startY, endY, options) {
    const { right } = this.screwPositions;
    let panelCenter = new THREE.Vector3();
    this.topPanel.mesh.getWorldPosition(panelCenter);

    const { object: line, text: htmlElement } = addDistanceLabel({
      top: {
        from: {
          x: panelCenter.x - right.y,
          y: panelCenter.y - startY,
          z: panelCenter.z
        },
        to: {
          x: panelCenter.x - right.y,
          y: panelCenter.y - endY - LabelsPadding,
          z: panelCenter.z
        }
      },
      bottom: {
        from: {
          x: 0,
          y: 0,
          z: 0
        },
        to: {
          x: 0,
          y: 0,
          z: 0
        }
      },
      size: this.ground.options.padding.C2x || 0,
      arrowDirection: Arrow.types.direction.X,
      labelText: (!options || !options.is3DMode || options.showLabels ? 'C2x = ' : '') + 
        `${unitTransform(+this.ground.options.padding.C2x).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.ground.options.padding.C2x).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      onChange: () => {}
    });

    htmlElement.position.set(
      panelCenter.x - right.y + this.ground.options.padding.C2x / 2,
      panelCenter.y - endY - LabelsPadding - 15,
      panelCenter.z
    );

    htmlElement.optimalCameraDist = 3000;
    htmlElement.zoomFactor = 0.5;
    if (!options.displayHorizontally) {
      htmlElement.setDirection(
        htmlElement.position.clone().setX(htmlElement.position.x + Math.max(25, this.ground.options.padding.C2x / 2)),
        htmlElement.position.clone().setX(htmlElement.position.x - Math.max(25, this.ground.options.padding.C2x / 2)),
        false
      );
    }

    return {
      line,
      htmlElement
    };
  }

  createDpotLabel(options) {
    const { object: line, text: htmlElement } = addDistanceLabel({
      top: {
        from: {
          y: 0,
          z: 0
        },
        to: {
          y: 0,
          z: 0
        }
      },
      bottom: {
        from: {
          y: 0,
          z: 0
        },
        to: {
          y: 0,
          z: 0
        }
      },
      size: this.options.barrier.dpot,
      arrowDirection: Arrow.types.direction.X,
      labelText: (options.showLabels ? 'Dpot: ' : '') + 
        `${unitTransform(+this.options.barrier.dpot).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.options.barrier.dpot).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      align: HTMLEditableLabel.types.align.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          barrier: {
            dpot: unitTransform(+value || 0, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        },
        false,
        didChange);
      }
    });

    const position = { ...this.panels.position };
    position.y -= this.barrier.size.height / 2;

    if (!options.displayHorizontally) {
      htmlElement.setDirection(
        new THREE.Vector3(line.position.x, line.position.y, line.position.z - this.options.barrier.dpot),
        line.position
      );
    }

    line.position.set(
      position.x,
      position.y,
      position.z - this.options.barrier.dpot / 2
    );

    htmlElement.position.set(
      position.x,
      position.y,
      position.z
    );

    line.rotation.z = Math.PI / -2;
    line.rotation.x = Math.PI / -2;

    return {
      line,
      htmlElement
    };
  }

  createHgsLabel(options) {
    const { object: line, text: htmlElement } = addDistanceLabel({
      top: {
        from: {
          y: 0,
          z: 0
        },
        to: {
          y: 0,
          z: 0
        }
      },
      bottom: {
        from: {
          y: 0,
          z: 0
        },
        to: {
          y: 0,
          z: 0
        }
      },
      size: this.options.barrier.height,
      arrowDirection: Arrow.types.direction.Z,
      labelText: (options.showLabels ? 'Hgc: ' : '') + 
      `${unitTransform(+this.options.barrier.height).from(defaultUnits.DISTANCE).to(Scene.units.distance)}`,
      labelValue: unitTransform(+this.options.barrier.height).from(defaultUnits.DISTANCE).to(Scene.units.distance),
      verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER,
      onChange: (value, didChange) => {
        this.dispatchOptionUpdate({
          barrier: {
            height: unitTransform(+value, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        },
        false,
        didChange);
      }
    });

    const position = { ...this.panels.position };

    position.y -= this.options.barrier.height;
    position.z -= this.barrier.size.width / 2;

    line.position.set(
      position.x,
      position.y,
      position.z
    );

    htmlElement.position.set(
      position.x,
      position.y + this.options.barrier.height / 2,
      position.z
    );

    line.rotation.z = Math.PI / -2;
    line.rotation.x = Math.PI / -2;

    return {
      line,
      htmlElement
    };
  }

  createShiftLabels(layer, options) {
    let panelCenter = new THREE.Vector3();
    this.topPanel.mesh.getWorldPosition(panelCenter);
    const shiftLabels = this.barrier.createShiftLabels(
      this.dispatchOptionUpdate, 
      this.options.beam.shift, 
      new THREE.Vector3(panelCenter.x, panelCenter.y, panelCenter.z),
      options
    );
    if (shiftLabels) {
      if (shiftLabels.marker) {
        shiftLabels.marker.position.set(panelCenter.x, panelCenter.y, panelCenter.z);
        if (options && options.is3DMode) {
          shiftLabels.marker.rotation.x = Math.PI / 2;
          shiftLabels.marker.position.z += this.topPanel.size.depth / 2;
        }
        layer.addObject(shiftLabels.marker);
      }
      if (shiftLabels.xLabel) {
        if (options && options.is3DMode) {
          // shiftLabels.xLabel.object.rotation.x = Math.PI / 2;
          shiftLabels.xLabel.object.position.z += this.topPanel.size.depth / 2;
          shiftLabels.xLabel.HTMLObject.optimalCameraDist = 3000;
          shiftLabels.xLabel.HTMLObject.zoomFactor = 0.5;
          shiftLabels.xLabel.HTMLObject.verticalAlign = HTMLEditableLabel.types.verticalAlign.CENTER;
          shiftLabels.xLabel.HTMLObject.position.set(
            shiftLabels.xLabel.HTMLObject.position.x,
            -shiftLabels.xLabel.HTMLObject.position.z + (this.options.beam.shift.y < 0 ? 15 : -15),
            shiftLabels.xLabel.HTMLObject.position.y + this.topPanel.size.depth / 2,
          );
          if (!options.displayHorizontally) {
            shiftLabels.xLabel.HTMLObject.setDirection(
              shiftLabels.xLabel.HTMLObject.position.clone().setX(
                shiftLabels.xLabel.HTMLObject.position.x + Math.max(25, this.options.beam.shift.x / 2)
              ), 
              shiftLabels.xLabel.HTMLObject.position.clone().setX(
                shiftLabels.xLabel.HTMLObject.position.x - Math.max(25, this.options.beam.shift.x / 2)
              ),
              false
            );
          }
        } else {
          shiftLabels.xLabel.object.rotation.set(-1.57, 1.57, 1.57, "ZXY");
          shiftLabels.xLabel.object.position.z += panelCenter.y;
          shiftLabels.xLabel.HTMLObject.position.z += panelCenter.y;
        }
        layer.addMesh(shiftLabels.xLabel.object);
        layer.htmlLayer.add(shiftLabels.xLabel.HTMLObject);
      }
      if (shiftLabels.yLabel) {
        if (options && options.is3DMode) {
          // shiftLabels.yLabel.object.rotation.x = Math.PI / 2;
          shiftLabels.yLabel.object.position.z += this.topPanel.size.depth / 2;
          shiftLabels.yLabel.HTMLObject.optimalCameraDist = 3000;
          shiftLabels.yLabel.HTMLObject.zoomFactor = 0.5;
          shiftLabels.yLabel.HTMLObject.align = HTMLEditableLabel.types.align.CENTER;
          shiftLabels.yLabel.HTMLObject.verticalAlign = HTMLEditableLabel.types.align.CENTER;
          shiftLabels.yLabel.HTMLObject.position.y *= -1;
          shiftLabels.yLabel.HTMLObject.position.set(
            shiftLabels.yLabel.HTMLObject.position.x + (this.options.beam.shift.x < 0 ? 15 : -15),
            -shiftLabels.yLabel.HTMLObject.position.z,
            -shiftLabels.yLabel.HTMLObject.position.y + this.topPanel.size.depth / 2,
          );
          if (!options.displayHorizontally) {
            shiftLabels.yLabel.HTMLObject.setDirection(
              shiftLabels.yLabel.HTMLObject.position.clone().setY(
                shiftLabels.yLabel.HTMLObject.position.y + Math.max(25, this.options.beam.shift.y / 2)
              ), 
              shiftLabels.yLabel.HTMLObject.position.clone().setY(
                shiftLabels.yLabel.HTMLObject.position.y - Math.max(25, this.options.beam.shift.y / 2)
              ),
              false
            );
          }
        } else {
          shiftLabels.yLabel.object.rotation.set(-1.57, 1.57, 1.57, "ZXY");
          shiftLabels.yLabel.object.position.z += panelCenter.y;
          shiftLabels.yLabel.HTMLObject.position.z += panelCenter.y;
        }
        layer.addMesh(shiftLabels.yLabel.object);
        layer.htmlLayer.add(shiftLabels.yLabel.HTMLObject);
      }
    }
  }

}



// WEBPACK FOOTER //
// ./src/object-view/generators/safety-barrier/objects/between-walls.js